import { EXTERNAL_OWNER_SYSTEMS } from '@/modules/file/constants'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { user } from 'ampersand-app'
import moment from 'moment'

const checkOcrValues = (expense, revenue, document) => {
  if (!document) {
    return {
      hasEmptyValues: false,
      hasUncheckedOcrValues: false,
      hasValidValues: false,
      values: {},
    }
  }

  const {
    accountHolder,
    bic,
    date,
    iban,
    invoiceNumber,
    paymentDueDate,
    totalAmount,
    payedAt,
    bankInstitute,
  } = document?.documentInvoiceDetails || {}

  const totalAmountValue = expense?.priceTotal || revenue?.priceTotal || totalAmount
  const invoiceNumberValue = expense?.receiptNumber || revenue?.receiptNumber || invoiceNumber
  const dateValue = expense?.date || revenue?.date || date
  const dueDateValue = expense?.dueDate || revenue?.dueDate || paymentDueDate
  const payedAtValue = expense?.payedAt || revenue?.payedAt || payedAt
  const bankInstituteValue = expense?.bankInstitute || revenue?.bankInstitute || bankInstitute

  // FLAG:useDocuAi — a DocuAI invoice pre-fills documentInvoiceDetails at ingest
  // (reviewStatus 'review', not yet reviewed), unlike NATIF where the extracted
  // values live in metaData.ocr and documentInvoiceDetails stays empty until the
  // operator confirms. So a populated field on a pending-review invoice is still
  // the UNCONFIRMED result and must show "review required" (yellow). Scoped to a
  // 'review' reviewStatus, so legacy/NATIF documents (no such reviewStatus here)
  // keep their per-field confirm-on-edit behaviour unchanged.
  // Gated on the tenant flag so that after a flag rollback (tenant ON→OFF) stale
  // reviewStatus='review' rows fall back to the legacy per-field behaviour — flag-OFF
  // stays baseline-identical by construction. The flag read is a plain ampersand-app +
  // constants lookup, so it does NOT reintroduce the circular import with
  // invoice-analysis-state (which imports this helper).
  const useDocuAiEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const isReviewedFlag = value => value === true || value === 'true' || value === 1 || value === '1'
  const isPendingReview = useDocuAiEnabled
    && document?.documentInvoiceDetails?.reviewStatus === 'review'
    && !isReviewedFlag(document?.metaData?.invoiceAnalysisReviewed)
    && !isReviewedFlag(document?.metaData?.ocr?.reviewed)

  const isOcrValue = (fieldValue, ocrValue, isDate = false) => {
    if (document?.externalOwnerSystem === EXTERNAL_OWNER_SYSTEMS.desktop) {
      return false
    }

    // Pending DocuAI review: any extracted value (in the field OR in metaData.ocr)
    // awaits operator confirmation, so flag it "pending review" until the review
    // is saved — even though documentInvoiceDetails is already populated.
    if (isPendingReview) {
      const hasValue = v => v !== undefined && v !== null && v !== ''
      return hasValue(fieldValue) || hasValue(ocrValue)
    }

    // If there's no OCR value, it can't be an OCR value
    if (!ocrValue) return false

    // If there's a non-empty field value, it's not an OCR value
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      return false
    }

    // For dates, validate the format
    if (isDate) {
      const ocrDate = moment(ocrValue, 'YYYY-MM-DD', true)
      return ocrDate.isValid()
    }

    // For non-dates, if we have OCR data but no field value, it's an OCR value
    return true
  }

  const ocrState = {
    priceTotal: isOcrValue(totalAmountValue, document?.metaData?.ocr?.priceTotal),
    receiptNumber: isOcrValue(invoiceNumberValue, document?.metaData?.ocr?.receiptNumber),
    date: isOcrValue(dateValue, document?.metaData?.ocr?.date, true),
    paymentDueDate: isOcrValue(dueDateValue, document?.metaData?.ocr?.paymentDueDate, true),
    payedAt: isOcrValue(payedAtValue, document?.metaData?.ocr?.payedAt, true),
    accountHolder: isOcrValue(accountHolder, document?.metaData?.ocr?.payee),
    iban: isOcrValue(iban, document?.metaData?.ocr?.payeeIban),
    bic: isOcrValue(bic, document?.metaData?.ocr?.payeeBic),
    bankInstitute: isOcrValue(bankInstituteValue, document?.metaData?.ocr?.payeeBank),
  }

  const values = {
    totalAmountValue: totalAmountValue || document?.metaData?.ocr?.priceTotal,
    invoiceNumberValue: invoiceNumberValue || document?.metaData?.ocr?.receiptNumber,
    dateValue: dateValue || document?.metaData?.ocr?.date,
    dueDateValue: dueDateValue || document?.metaData?.ocr?.paymentDueDate,
    payedAtValue: payedAtValue || document?.metaData?.ocr?.payedAt,
    accountHolder: accountHolder || document?.metaData?.ocr?.payee,
    iban: iban || document?.metaData?.ocr?.payeeIban,
    bic: bic || document?.metaData?.ocr?.payeeBic,
    bankInstitute: bankInstituteValue || document?.metaData?.ocr?.payeeBank,
  }

  const hasValidValues = [
    dateValue || document?.metaData?.ocr?.date,
    document?.metaData?.ocr?.supplier,
    document?.metaData?.ocr?.customer,
    invoiceNumberValue || document?.metaData?.ocr?.receiptNumber,
    accountHolder || document?.metaData?.ocr?.payee,
    document?.metaData?.ocr?.ocrPayee,
    totalAmountValue || document?.metaData?.ocr?.priceTotal,
    dueDateValue || document?.metaData?.ocr?.paymentDueDate,
    payedAtValue || document?.metaData?.ocr?.payedAt,
    iban || document?.metaData?.ocr?.payeeIban,
    bic || document?.metaData?.ocr?.payeeBic,
  ].some(value => value !== undefined && value !== null && value !== '')

  return {
    ...ocrState,
    hasUncheckedOcrValues: Object.values(ocrState).some((value) => !!value),
    hasValidValues,
    hasEmptyValues: Object.values(values).some((value) => !value),
    values,
  }
}

export default checkOcrValues
